import { config } from "./config";
import type { PlanId } from "./funnelData";

export type CheckoutOfferId = "lifetime" | "polar_trial_7d";
export type CheckoutProvider = "stripe" | "polar";

export type CheckoutOffer = {
  id: CheckoutOfferId;
  provider: CheckoutProvider;
  analyticsKey: string;
  trialDays: number;
  priceDisplay: string;
  priceAmount: number;
  currency: string;
  renewalCopy: string;
  renewalDisplay: Partial<Record<PlanId, string>>;
};

const TRIAL_OFFER_QUERY_VALUES = new Set([
  "trial-yearly",
  "trial_yearly",
  "trial-7d",
  "trial_7d",
  "free-trial",
  "free_trial",
  "polar-trial",
  "polar_trial",
  "polar_trial_7d",
]);

const parsePositiveInteger = (value: string, fallback: number): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const parsePositiveNumber = (value: string, fallback: number): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const defaultCheckoutOffer: CheckoutOffer = {
  id: "lifetime",
  provider: "stripe",
  analyticsKey: "lifetime",
  trialDays: 0,
  priceDisplay: "",
  priceAmount: 0,
  currency: "USD",
  renewalCopy: "",
  renewalDisplay: {},
};

export const getCheckoutOfferFromLocation = (location: Location): CheckoutOffer => {
  const params = new URLSearchParams(location.search);
  const rawOffer = params.get("offer") ?? params.get("checkout_offer") ?? "";
  const normalizedOffer = rawOffer.trim().toLowerCase();

  if (!TRIAL_OFFER_QUERY_VALUES.has(normalizedOffer)) {
    return defaultCheckoutOffer;
  }

  return {
    id: "polar_trial_7d",
    provider: "polar",
    analyticsKey: "polar_trial_7d",
    trialDays: parsePositiveInteger(config.polarTrialDays, 7),
    priceDisplay: config.polarTrialPriceDisplay,
    priceAmount: parsePositiveNumber(config.polarTrialPriceAmount, 0),
    currency: config.polarTrialCurrency.toUpperCase(),
    renewalCopy: config.polarTrialRenewalCopy,
    renewalDisplay: {
      standard: config.polarTrialStandardRenewalDisplay,
      pro: config.polarTrialProRenewalDisplay,
    },
  };
};

export const isPolarTrialOffer = (offer: CheckoutOffer): boolean => offer.id === "polar_trial_7d";
